// "@" mention tokens (#12154), shared by the composer (ChatInputBar) and the
// sent bubble (UserMessage). A mention is "@<record name>" at a word
// boundary: start of message or after whitespace, and not followed by a
// letter, digit or underscore. Names contain spaces, so a mention cannot be
// recovered from the text alone: the caller supplies the records it knows.

import type { ReferencedEntity } from "../types";
import type { EntityKind } from "../../RMS/Dashboard/types";

// Every record kind, keyed so that a kind added to EntityKind is a compile
// error here until it is listed.
const RECORD_KINDS: Record<EntityKind, true> = {
  person: true,
  company: true,
  manager: true,
  vehicle: true,
  topic: true,
  interaction: true,
  client: true,
  portfolio: true,
  deal: true,
};

/** Narrows a kind read off the wire (a stored event) to one the app models. */
export const isEntityKind = (kind: unknown): kind is EntityKind =>
  typeof kind === "string" && Object.hasOwn(RECORD_KINDS, kind);

const escapeRegExp = (text: string): string =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Where each known record's "@Name" token sits in `message`, as [start, end)
 * ranges with the record it spells, in order, never overlapping. Longer names
 * win a tie, so "@Acme Capital" is one mention, not "@Acme" and more text.
 * Two records sharing a name resolve to the first one given. */
export function findMentions(
  message: string,
  entities: ReferencedEntity[]
): Array<{ start: number; end: number; entity: ReferencedEntity }> {
  const found: Array<{ start: number; end: number; entity: ReferencedEntity }> =
    [];
  const byName = new Map<string, ReferencedEntity>();
  for (const entity of entities) {
    if (entity.name.length > 0 && !byName.has(entity.name)) {
      byName.set(entity.name, entity);
    }
  }
  const byLength = [...byName.values()].sort(
    (a, b) => b.name.length - a.name.length
  );
  for (const entity of byLength) {
    const pattern = new RegExp(
      `(^|\\s)@${escapeRegExp(entity.name)}(?![\\p{L}\\p{N}_])`,
      "gu"
    );
    for (const match of message.matchAll(pattern)) {
      const start = match.index + match[1].length;
      const end = start + entity.name.length + 1;
      if (found.some((m) => start < m.end && end > m.start)) continue;
      found.push({ start, end, entity });
    }
  }
  return found.sort((a, b) => a.start - b.start);
}

/** The chosen records whose "@Name" is still in the message, in the order
 * chosen, one per record. A mention whose text was edited is gone. */
export function liveReferencedEntities(
  message: string,
  chosen: ReferencedEntity[]
): ReferencedEntity[] {
  const spelled = new Set(
    findMentions(message, chosen).map((m) => m.entity.name)
  );
  const seen = new Set<string>();
  return chosen.filter((entity) => {
    if (seen.has(entity.id) || !spelled.has(entity.name)) return false;
    seen.add(entity.id);
    return true;
  });
}
